import { ReactNode, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar } from "./AdminSidebar";
import { UniversalSearch } from "./UniversalSearch";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Search, CheckCheck } from "lucide-react";
import NotificationCenter from "./NotificationCenter";
import { useAdminNotifications, AdminNotification } from "@/hooks/useAdminNotifications";
import { useAdminActivityBadges, type AdminSection } from "@/hooks/useAdminActivityBadges";

import { PageContextProvider } from "./eos/PageContext";
import { AssistantConversationProvider } from "./eos/AssistantConversation";
import { EosDock } from "./eos/EosDock";

interface AdminShellProps {
  children: ReactNode;
  onOpenNotification?: (n: AdminNotification) => void;
  /** When set, shows a "Mark all reviewed" button in the header for this section. */
  section?: AdminSection;
  /** When true, hides the permanent MAVIS dock and reclaims the right padding. */
  hideDock?: boolean;
}

export function AdminShell({ children, onOpenNotification, section, hideDock = false }: AdminShellProps) {
  const { user, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();

  const [searchOpen, setSearchOpen] = useState(false);

  const notif = useAdminNotifications(user?.id);
  const badges = useAdminActivityBadges(!!user && isAdmin);
  const sectionCount = section ? badges.counts[section] ?? 0 : 0;

  // Routing/auth gating lives in AdminGuard (see src/components/admin/AdminGuard.tsx).
  // A second redirect here used to fire during token refreshes and bounce the
  // admin out of the page they were working on, so the shell no longer navigates.

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);


  const handleNotif = (n: AdminNotification) => {
    if (onOpenNotification) {
      onOpenNotification(n);
      return;
    }
    // Fallback: route to relevant admin section
    if (n.itemType === "popup_vendor" || n.itemType === "popup_ticket") navigate("/admin/popup");
    else if (n.itemType === "instructor_proposal") navigate("/admin/instructors");
    else if (n.itemType === "interest")
      navigate(`/admin/registrations?focusInterest=${encodeURIComponent(n.itemId)}`);
    else if (n.itemType === "payment")
      navigate(`/admin/registrations?focusPayment=${encodeURIComponent(n.itemId)}`);
    else navigate("/admin");
    notif.markAsRead(n.itemType as any, n.itemId);
  };

  return (
    <SidebarProvider>
      <PageContextProvider>
        <AssistantConversationProvider>
          <div className="min-h-screen flex w-full bg-background">
            <AdminSidebar />
            <div className={`flex-1 flex flex-col min-w-0 ${hideDock ? "" : "lg:pr-[360px] xl:pr-[400px]"}`}>

              <header className="sticky top-0 z-30 h-14 border-b border-border bg-card/95 backdrop-blur flex items-center gap-2 px-3">
                <SidebarTrigger />
                <div className="flex-1 max-w-xl">
                  <button
                    onClick={() => setSearchOpen(true)}
                    className="w-full flex items-center gap-2 h-9 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground hover:border-primary/40 transition-colors"
                  >
                    <Search className="h-4 w-4" />
                    <span className="flex-1 text-left">Search parents, classes, instructors, vendors…</span>
                    <kbd className="hidden sm:inline-flex items-center h-5 px-1.5 rounded border border-border bg-muted text-[10px] font-mono">⌘K</kbd>
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  {section && sectionCount > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => badges.markSectionReviewed(section)}
                      className="hidden md:inline-flex gap-1.5"
                      title={`Mark all ${sectionCount} unreviewed item${sectionCount === 1 ? "" : "s"} in this section as reviewed`}
                    >
                      <CheckCheck className="h-4 w-4" />
                      Mark all reviewed
                      <span className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center leading-none">
                        {sectionCount > 99 ? "99+" : sectionCount}
                      </span>
                    </Button>
                  )}
                  <NotificationCenter
                    notifications={notif.notifications}
                    unreadCount={notif.totalUnread}
                    onOpenItem={handleNotif}
                    onMarkAllRead={notif.markAllRead}
                    onResend={notif.resendEmail}
                    isResending={notif.isResending}
                  />
                  <Link to="/">
                    <Button variant="ghost" size="sm" className="hidden md:inline-flex">
                      View Site
                    </Button>
                  </Link>
                  <Button variant="ghost" size="icon" onClick={signOut} title="Sign out">
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </header>
              <main className="flex-1 overflow-y-auto">
                {children}
              </main>
            </div>
            <EosDock floating={hideDock} />
          </div>
          <UniversalSearch open={searchOpen} onOpenChange={setSearchOpen} />
        </AssistantConversationProvider>
      </PageContextProvider>

    </SidebarProvider>
  );
}
